/**
 * 🌊 **Whether your eye is under water, and how far down** — T61.
 *
 * 🎯 **haugur's screenshots from a metre under the sea are a dry gravel plain
 * with fish hanging in the air.** Everything that makes the sea look like the
 * sea — the fog, the dimming, the surface overhead — hangs off one question
 * this game has never been able to answer: **am I in the water?**
 *
 * ⚠️ **Nothing in the client knew.** Grepped before this file was written: no
 * `underwater`, `submerged`, `inWater` or `waterAt` anywhere in
 * `packages/client/src`.
 *
 * ## 🔴 Why this is its own file, and its own test
 *
 * **The look cannot be tested here and this can.** A fog colour is judged by
 * eye on a machine with a GPU, which this workstation is not. **The decision
 * underneath it is arithmetic**, and arithmetic with one nasty edge — so it is
 * separated from the drawing and pinned.
 *
 * ## 🔴 THE EDGE, AND IT IS THE WHOLE REASON FOR THE `known` ARGUMENT
 *
 * `blockAt` in `main.ts` **returns air for a chunk that has not arrived yet**.
 * That is deliberate and documented — the shelter readout says so in as many
 * words: *"a roof still streaming in reads as open sky for a moment."*
 *
 * ⚠️ **Here it would be much worse than a wrong word on a HUD.** Walk into the
 * sea, look around, and the chunks behind you are rebuilt: for the frames where
 * the chunk under your eye is missing, the world **is not air — it is
 * unknown** — and a naive reading flips the sea off and back on.
 * 🔴 **That reads as a rendering bug, not as weather**, and it is exactly the
 * class of fault this project keeps shipping green.
 *
 * ✅ **So an unknown chunk carries the previous answer forward** rather than
 * answering air. **`stale` says when that happened**, so a diagnostic can tell
 * *the world has not arrived* from *you are not in water*.
 *
 * ## 📖 The research this follows
 *
 * [`Development/Minecraft/20_Water_Seen_From_Inside.md`](../../../Development/Minecraft/20_Water_Seen_From_Inside.md)
 * §5: Minecraft runs **two** submersion tests that disagree on purpose — the
 * camera per frame drives the fog, the eye per tick drives the overlay and the
 * breath. ⚠️ **We have one, because we have one thing to drive.** When drowning
 * arrives (T134) it will want the second, and it should not be bolted onto this
 * one — *a second way in that forgets what the first one does* is recorded six
 * times in this repo.
 */

import { isFluid } from '@lofi/shared';

/** What the eye is in, this frame. */
export interface Submersion {
  /** Is the eye inside a fluid block? */
  readonly submerged: boolean;
  /**
   * How far the eye is below the surface, in metres. **0 when not submerged.**
   *
   * ⚠️ **Measured to the top of the fluid above the eye**, not to sea level —
   * a river on a hillside has its own surface, and D114's waterfalls stand
   * water well above it.
   */
  readonly depth: number;
  /**
   * 🔴 **True when the world under the eye had not arrived and the previous
   * answer was carried forward.** Not a fault; the alternative is a sea that
   * blinks. A diagnostic should show it rather than hide it.
   */
  readonly stale: boolean;
}

/** Nothing known yet, and not in water — what a session starts at. */
export const DRY: Submersion = { submerged: false, depth: 0, stale: false };

/**
 * 🔴 **How far up the search for the surface goes, in metres.**
 *
 * 📏 **64 is chosen against the world rather than guessed.** The deepest sea in
 * the start box is a few metres; the model's own vertical band is 160 m
 * (`CY_BELOW`), and the dimming this feeds saturates by 15 m — Minecraft's
 * water costs one sky light per metre and the world is black at 15.
 * **So anything past 64 m cannot change what is drawn**, and walking further
 * would be work with no effect on the picture.
 *
 * ⚠️ **A cap that is reached is reported as the cap**, so a very deep column
 * dims at its deepest and does not read as shallow.
 */
export const MAX_DEPTH_PROBE = 64;

/**
 * Is the eye under water, and how deep?
 *
 * @param eye      the camera's position in WORLD metres. 🔴 **Not
 *   `camera.position`** — `main.ts` rebases that into render-origin space, and
 *   measuring from it is the fault that hid every nameplate for a day.
 * @param blockAt  the client's own block lookup, world coordinates
 * @param known    whether the chunk holding that block has actually arrived.
 *   🔴 **Without this, `blockAt`'s air-for-a-missing-chunk makes the sea blink.**
 * @param previous last frame's answer, carried forward while the world loads
 */
export function submersionAt(
  eye: { readonly x: number; readonly y: number; readonly z: number },
  blockAt: (x: number, y: number, z: number) => number,
  known: (x: number, y: number, z: number) => boolean,
  previous: Submersion = DRY,
): Submersion {
  const ex = Math.floor(eye.x);
  const ey = Math.floor(eye.y);
  const ez = Math.floor(eye.z);

  /* 🔴 The unknown case FIRST, before anything is read. An unanswered question
     is not a "no", and treating it as one is the whole bug. */
  if (!known(ex, ey, ez)) {
    return { submerged: previous.submerged, depth: previous.depth, stale: true };
  }

  if (!isFluid(blockAt(ex, ey, ez))) return DRY;

  /* ⬆️ Walk up to the surface. The first block that is not fluid is above the
     water, so the surface is at its bottom face. */
  let top = ey;
  while (top - ey < MAX_DEPTH_PROBE) {
    const above = top + 1;
    /* ⚠️ An unknown block above is treated as the surface rather than carried
       forward. It makes the water read as SHALLOWER than it is for a frame,
       which brightens; the alternative reads as deeper, which darkens the
       world around somebody who may be standing in the shallows. **Err
       towards the brighter mistake** — a too-dark frame looks broken and a
       too-bright one looks like clear water. */
    if (!known(ex, above, ez) || !isFluid(blockAt(ex, above, ez))) break;
    top = above;
  }

  /* 📏 The eye's own height inside its block counts: an eye at y = 10.9 under a
     surface at 11.0 is a tenth of a metre down, not a whole one. */
  const surface = top + 1;
  const depth = Math.max(0, surface - eye.y);

  return { submerged: true, depth, stale: false };
}

/* ──────────────────────────────────────────────────────────────────────────
   🎨 WHAT IT LOOKS LIKE — the numbers, in one place
   ────────────────────────────────────────────────────────────────────────── */

/**
 * 🌊 **The colour of shallow Icelandic sea water.**
 *
 * 🔴 **Deliberately NOT the colour of the water's surface**, and that is read
 * off the research rather than chosen by taste. `Minecraft/20`: Java's water fog
 * is `#050533` — 90% black — while its surface is `#3F76E4`. **Bedrock makes the
 * two the same and its water reads as a swimming pool.**
 *
 * ⚠️ **Ours is a cold glacial green, because this is the North Atlantic off
 * Hraunin and not a tropical lagoon.**
 *
 * 🎯 **CHOSEN BY haugur, 2026-09-21, off `Design/samples/99_water_colour.png`:
 * *"i selsect 4"*** — after he played the first attempt and filed #701,
 * *"this is how it looks, sky is black."* 🔴 **The first value was picked off
 * a sheet that did no tone mapping**, so it was chosen against a picture the
 * game never draws.
 */
export const SHALLOW_FOG = 0x63a0a4;

/**
 * 🌑 **What the water fades towards as you go down.**
 *
 * ⚠️ **Not pure black.** A sea that reaches `#000000` reads as the world having
 * failed to draw rather than as deep water, which is the same reason
 * `WATER_OPACITY_MIN` is not 0.
 */
export const DEEP_FOG = 0x1e424c;

/**
 * 📏 **How deep before it is as dark as it gets, in metres.**
 *
 * 🎯 **26, chosen by haugur on 2026-09-21** — panel ④, *"lighter and
 * slower"*. ⚠️ **It was 15, which is Minecraft's number arriving by a
 * different road**: its water costs one sky light per metre, so at 15 m the
 * world is lit at zero and reads as a cave. **We have no light grid at all**, so
 * the distance is applied to the fog colour and to the scene lights instead.
 *
 * 🔴 **THIS NUMBER DOES TWO JOBS, AND THE SHEET ONLY SHOWED ONE.** It sets how
 * fast the colour fades AND how fast the world dims — but every panel on sheet
 * 99 was lit the same, so **the slower DIMMING that comes with 26 m was never on
 * the picture he chose from.** ⚠️ **He was told before it shipped.**
 */
export const FULL_DARK_DEPTH = 26;

/**
 * 🌫️ **How far you can see under water, in metres.**
 *
 * 📏 **Minecraft's number is 60** — Bedrock's water `fog_end`, the only tier-1
 * figure in the research. ⚠️ **It shipped at 60 and could barely be seen**, and
 * the reason is geometry rather than taste.
 *
 * 🔴 **THE ARITHMETIC, BECAUSE IT DECIDES THE NUMBER.** With your eye 1.6 m
 * above a flat seabed, ground **60 m** away sits **1.5° below the horizon**,
 * and the bottom of a 75° view is ground **2.1 m** away. **So nearly every
 * scrap of seabed on screen is far closer than 60 m**, where a 60 m fog is
 * only a few per cent thick. The fog was real, correct, and invisible.
 *
 * ✅ **30 m makes the same view fade from 7% at your feet to solid at the
 * horizon**, which is what being under water looks like.
 *
 * 👤 **It is a look, so it is haugur's** — and it is one line.
 */
export const UNDERWATER_FOG_FAR = 30;

/**
 * 🔴 **Underwater the fog starts AT THE CAMERA, and this is the single biggest
 * change in the whole ticket.**
 *
 * In air ours starts at `CHUNK_SIZE * 3` — 96 m out. `Minecraft/20`: *"air fog
 * and water fog are the same fog with different numbers"*, and water's starts at
 * zero. **Most of what makes water look like water is this one line.**
 */
export const UNDERWATER_FOG_NEAR = 0;

/**
 * 🌑 **How dark the world goes at the bottom of {@link FULL_DARK_DEPTH}**, as a
 * share of its lit brightness.
 *
 * ⚠️ **Not zero, and the reason is haugur's fish.** Pitch black is accurate and
 * useless: he has spent a week making four fish worth looking at, and the whole
 * point of clear water was being able to see them.
 *
 * 🔴 **The fish do NOT dim with this**, which is what makes it safe to use at
 * all: `fishbody.ts` lights a fish with **baked arithmetic, deliberately not the
 * scene's sun.** **The world darkens around them and they do not.**
 */
export const DEEP_LIGHT_FLOOR = 0.25;

/** 0 at the surface, 1 at {@link FULL_DARK_DEPTH} and below. */
export function darkness(depth: number): number {
  return Math.min(1, Math.max(0, depth / FULL_DARK_DEPTH));
}

/**
 * The fog colour at this depth, as a packed `0xRRGGBB`.
 *
 * ⚠️ **Channel by channel in plain arithmetic**, not through a colour library —
 * this has to be callable from a design sheet and from a test as well as from
 * the renderer, and `three` is a heavy import for a lerp.
 */
export function fogColourAt(depth: number): number {
  const t = darkness(depth);
  const mix = (shift: number): number => {
    const a = (SHALLOW_FOG >> shift) & 0xff;
    const b = (DEEP_FOG >> shift) & 0xff;
    return Math.round(a + (b - a) * t) & 0xff;
  };
  return (mix(16) << 16) | (mix(8) << 8) | mix(0);
}

/**
 * How much of its ordinary brightness the world keeps at this depth.
 *
 * 🔴 **This is the depth dimming, and it is NOT how Minecraft does it.**
 * Minecraft dims by filtering sky light through the water, block by block. **We
 * have no light grid**, so the same effect is reached by scaling the scene's
 * own lights — see {@link FULL_DARK_DEPTH}.
 */
export function lightScaleAt(depth: number): number {
  return 1 - (1 - DEEP_LIGHT_FLOOR) * darkness(depth);
}
